import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Container, Row, Col } from 'reactstrap';
import CountUp from 'react-countup';

interface StatCardProps {
  value: number;
  suffix?: string;
  prefix?: string;
  label: string;
  icon: React.ReactNode;
  index: number;
}

const StatCard: React.FC<StatCardProps> = ({ value, suffix, prefix, label, icon, index }) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      className="stat-card-trust"
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={prefersReducedMotion ? {} : { y: -5, scale: 1.02 }}
    >
      <div className="stat-icon-wrapper">{icon}</div>
      <div className="stat-value">
        {prefix}
        <CountUp
          end={value}
          duration={2.5}
          separator=","
          enableScrollSpy
          scrollSpyOnce
        />
        {suffix}
      </div>
      <div className="stat-label">{label}</div>
    </motion.div>
  );
};

interface TestimonialProps {
  quote: string;
  author: string;
  role: string;
  avatar: string;
  rating: number;
  index: number;
}

const Testimonial: React.FC<TestimonialProps> = ({ quote, author, role, avatar, rating, index }) => {
  return (
    <motion.div
      className="testimonial-card"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
    >
      {/* Rating */}
      <div className="testimonial-rating">
        {[...Array(5)].map((_, i) => (
          <svg
            key={i}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill={i < rating ? "#f59e0b" : "none"}
            stroke="#f59e0b"
            strokeWidth="2"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        ))}
      </div>

      {/* Quote */}
      <p className="testimonial-quote">"{quote}"</p>

      {/* Author */}
      <div className="testimonial-author">
        <div className="author-avatar">
          {avatar}
        </div>
        <div className="author-info">
          <span className="author-name">{author}</span>
          <span className="author-role">{role}</span>
        </div>
      </div>
    </motion.div>
  );
};

const TrustStats: React.FC = () => {
  const stats = [
    {
      value: 85,
      suffix: "%",
      label: "Average Time Saved",
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
    },
    {
      value: 5000,
      suffix: "+",
      label: "Listings Created Daily",
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      ),
    },
    {
      value: 24,
      suffix: "/7",
      label: "Automated Updates",
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M23 4v6h-6M1 20v-6h6" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      ),
    },
    {
      value: 98,
      suffix: "%",
      label: "Customer Satisfaction",
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
        </svg>
      ),
    },
  ];

  const testimonials = [
    {
      quote: "CodeRom transformed my business. What used to take hours now takes minutes. The AI category matching is incredibly accurate.",
      author: "James M.",
      role: "eBay PowerSeller",
      avatar: "JM",
      rating: 5,
    },
    {
      quote: "The repricer alone paid for my subscription in the first week. I'm winning more Buy Boxes and my margins have improved significantly.",
      author: "Sarah L.",
      role: "Multi-Channel Seller",
      avatar: "SL",
      rating: 5,
    },
    {
      quote: "Finally, a tool that understands dropshipping. The VeRO protection has saved me from countless potential suspensions.",
      author: "David R.",
      role: "Full-time Dropshipper",
      avatar: "DR",
      rating: 5,
    },
  ];

  return (
    <section className="trust-stats-section">
      <Container>
        {/* Stats Grid */}
        <motion.div
          className="section-header centered"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-badge">
            <span className="badge-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </span>
            <span>Trusted by Sellers</span>
          </div>
          <h2 className="section-title">
            Join thousands of <span className="gradient-text">successful sellers</span>
          </h2>
          <p className="section-subtitle">
            Real results from real dropshippers using CodeRom every day.
          </p>
        </motion.div>

        {/* Stats Cards */}
        <Row className="stats-row">
          {stats.map((stat, index) => (
            <Col lg={3} md={6} key={index} className="mb-4">
              <StatCard {...stat} index={index} />
            </Col>
          ))}
        </Row>

        {/* Testimonials */}
        <div className="testimonials-wrapper">
          <Row className="justify-content-center">
            {testimonials.map((testimonial, index) => (
              <Col lg={4} md={6} key={index} className="mb-4">
                <Testimonial {...testimonial} index={index} />
              </Col>
            ))}
          </Row>
        </div>
      </Container>

      {/* Background */}
      <div className="trust-stats-bg">
        <div className="gradient-mesh" />
      </div>
    </section>
  );
};

export default TrustStats;
